import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { IEntity } from '@proximus/entity-manager';
import * as MailboxesActions from '../actions/mailbox.actions';
import { MailboxData, MailboxEntryData } from '../../models/mailboxes.model';
import { ErrorCode, TechnicalError } from '../../models/errors.model';
import { ApplicationState, getCustomerId } from '../../../store/reducers';
import { MailboxesService } from '../../service/mailboxes.service';
import { CustomerService } from '@proximus/ng-common';
import { CustomerAccount } from '@proximus/common-business-logic';
import { Observable, of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { BrandService } from 'src/app/services/brand.service';

@Injectable()
export class MailboxesEffects {
  @Effect()
  userMailboxes$: Observable<Action> = this.actions$.pipe(
    ofType(MailboxesActions.ActionTypes.LOAD),
    switchMap(() => this.customerService.getCustomerAccount()),
    switchMap((customerAccount: CustomerAccount) =>
      this.mailboxesService.getMailboxes(customerAccount, this.brandService.getAppBrandValue()),
    ),
    map(
      (mailboxDataEntity: IEntity<MailboxData>) =>
        new MailboxesActions.LoadedAction(mailboxDataEntity.value),
    ),
    catchError((error: IEntity<TechnicalError>) =>
      of(
        new MailboxesActions.LoadFailAction({
          message: '',
          code: ErrorCode.MailboxLoadFail,
        }),
      ),
    ),
  );

  @Effect()
  createMailbox$: Observable<Action> = this.actions$.pipe(
    ofType(MailboxesActions.ActionTypes.CREATE),
    map((action: MailboxesActions.CreateAction) => action.payload),
    withLatestFrom(this.store.select(getCustomerId)),
    switchMap(([action, cdbId]) =>
      this.mailboxesService
        .createMailbox(cdbId, action.aliasId, action.password)
        .pipe(
          mergeMap((success: IEntity<MailboxEntryData>) => [
            new MailboxesActions.CreateSuccessAction(success.value),
            new MailboxesActions.LoadAction(),
          ]),
          catchError((error: IEntity<TechnicalError>) =>
            of(new MailboxesActions.CreateFailAction(error.value)),
          ),
        ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<ApplicationState>,
    private mailboxesService: MailboxesService,
    private customerService: CustomerService,
    private readonly brandService: BrandService,
  ) {}
}
